import React, { useState, useEffect } from 'react';
import {
  MessageSquare, Mail, Calendar, ListTodo, FileText, Hash, User,
  X, Search, Loader2, Send, Plus, Clock, Users, Briefcase, Phone, ChevronDown
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Quick Action Button Component
const QuickActionButton = ({ icon: Icon, label, onClick, color = "text-gray-600" }) => (
  <Button
    variant="ghost"
    size="sm"
    onClick={onClick}
    className="flex flex-col items-center gap-1 h-auto py-2 px-3 hover:bg-slate-100 dark:hover:bg-slate-800"
  >
    <Icon className={`h-4 w-4 ${color}`} />
    <span className="text-xs font-medium">{label}</span>
  </Button>
);

// Contact Picker Component
const ContactPicker = ({ value, onChange, label = "Contact" }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && search.length >= 2) {
      const searchContacts = async () => {
        setLoading(true);
        try {
          const res = await axios.get(`${API}/contacts/search?q=${encodeURIComponent(search)}&limit=10`);
          setContacts(res.data.contacts || []);
        } catch (error) {
          // Fallback to customers search
          try {
            const res = await axios.get(`${API}/customers?search=${encodeURIComponent(search)}&limit=10`);
            setContacts(res.data.customers || res.data || []);
          } catch (e) {
            console.error('Contact search error:', e);
          }
        }
        setLoading(false);
      };
      const timer = setTimeout(searchContacts, 300);
      return () => clearTimeout(timer);
    }
  }, [open, search]);

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" className="w-full justify-between">
            {value ? (
              <span className="truncate">{value.name} {value.phone ? `• ${value.phone}` : ''}</span>
            ) : (
              <span className="text-muted-foreground">Search contacts...</span>
            )}
            <ChevronDown className="h-4 w-4 ml-2 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0" align="start">
          <Command>
            <CommandInput 
              placeholder="Type to search..." 
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              {loading && (
                <div className="p-4 text-center">
                  <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                </div>
              )}
              <CommandEmpty>No contacts found</CommandEmpty>
              <CommandGroup>
                {contacts.map((contact) => (
                  <CommandItem
                    key={contact.id}
                    value={contact.name}
                    onSelect={() => {
                      onChange(contact);
                      setOpen(false);
                    }}
                  >
                    <User className="h-4 w-4 mr-2" />
                    <div className="flex-1">
                      <div className="font-medium">{contact.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {contact.phone || contact.email}
                      </div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

// Job Picker Component
const JobPicker = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && search.length >= 2) {
      const searchJobs = async () => {
        setLoading(true);
        try {
          const res = await axios.get(`${API}/jobs?search=${encodeURIComponent(search)}&limit=10`);
          setJobs(res.data.jobs || res.data || []);
        } catch (error) {
          console.error('Job search error:', error);
        }
        setLoading(false);
      };
      const timer = setTimeout(searchJobs, 300);
      return () => clearTimeout(timer);
    }
  }, [open, search]);

  return (
    <div className="space-y-2">
      <Label>Link to Job (optional)</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" className="w-full justify-between">
            {value ? (
              <span className="truncate">{value.customer_name || value.title || `Job #${value.id?.slice(0,8)}`}</span>
            ) : (
              <span className="text-muted-foreground">Search jobs...</span>
            )}
            <ChevronDown className="h-4 w-4 ml-2 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0" align="start">
          <Command>
            <CommandInput 
              placeholder="Search by customer or job ID..." 
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              {loading && (
                <div className="p-4 text-center">
                  <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                </div>
              )}
              <CommandEmpty>No jobs found</CommandEmpty>
              <CommandGroup>
                {jobs.map((job) => (
                  <CommandItem
                    key={job.id}
                    value={job.customer_name || job.id}
                    onSelect={() => {
                      onChange(job);
                      setOpen(false);
                    }}
                  >
                    <Briefcase className="h-4 w-4 mr-2" />
                    <div className="flex-1">
                      <div className="font-medium">{job.customer_name || `Job #${job.id?.slice(0,8)}`}</div>
                      <div className="text-xs text-muted-foreground">
                        {job.status} • {job.address?.slice(0, 30)}
                      </div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {value && (
        <Button variant="ghost" size="sm" onClick={() => onChange(null)} className="text-xs">
          Clear selection
        </Button>
      )}
    </div>
  );
};

// User Picker Component
const UserPicker = ({ value, onChange, label = "User" }) => {
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && users.length === 0) {
      const fetchUsers = async () => {
        setLoading(true);
        try {
          const res = await axios.get(`${API}/users?limit=50`);
          setUsers(res.data.users || res.data || []);
        } catch (error) {
          console.error('Users fetch error:', error);
        }
        setLoading(false);
      };
      fetchUsers();
    }
  }, [open, users.length]);

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" className="w-full justify-between">
            {value ? (
              <span className="truncate">{value.name || `${value.first_name} ${value.last_name}`}</span>
            ) : (
              <span className="text-muted-foreground">Select user...</span>
            )}
            <ChevronDown className="h-4 w-4 ml-2 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-0" align="start">
          <Command>
            <CommandInput placeholder="Search users..." />
            <CommandList>
              {loading && (
                <div className="p-4 text-center">
                  <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                </div>
              )}
              <CommandEmpty>No users found</CommandEmpty>
              <CommandGroup>
                {users.map((user) => (
                  <CommandItem
                    key={user.id}
                    value={user.name || `${user.first_name} ${user.last_name}`}
                    onSelect={() => {
                      onChange(user);
                      setOpen(false);
                    }}
                  >
                    <User className="h-4 w-4 mr-2" />
                    {user.name || `${user.first_name} ${user.last_name}`}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

// ============== COMPOSERS ==============

// SMS Composer - Enhanced with templates and character count
const SMSComposer = ({ open, onClose, onInsert, onSubmit }) => {
  const [contact, setContact] = useState(null);
  const [job, setJob] = useState(null);
  const [message, setMessage] = useState('');
  const [template, setTemplate] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const SMS_TEMPLATES = {
    '': { name: 'No Template', message: '' },
    'appointment_reminder': { 
      name: 'Appointment Reminder', 
      message: 'Hi! This is a reminder for your upcoming appointment. Please reply YES to confirm or call us to reschedule.'
    },
    'on_the_way': { 
      name: 'On My Way', 
      message: 'Hi! Just wanted to let you know I\'m on my way to your location. I should arrive in approximately 15-20 minutes.'
    },
    'follow_up': { 
      name: 'Follow-up', 
      message: 'Hi! Just following up on our recent conversation. Please let me know if you have any questions.'
    },
    'thank_you': { 
      name: 'Thank You', 
      message: 'Thank you for choosing us! We appreciate your business. Please reach out if you need anything.'
    },
    'quote_ready': { 
      name: 'Quote Ready', 
      message: 'Great news! Your quote is ready. Please check your email or give us a call to review the details.'
    }
  };

  const handleTemplateChange = (templateKey) => {
    setTemplate(templateKey);
    if (SMS_TEMPLATES[templateKey]?.message) {
      setMessage(SMS_TEMPLATES[templateKey].message);
    }
  };

  const generateActionBlock = () => {
    if (!contact?.phone || !message.trim()) return null;
    
    let block = `[ACTION:SMS]\n`;
    block += `to_contact_id=${contact.id}\n`;
    block += `to_phone=${contact.phone}\n`;
    if (job) block += `job_id=${job.id}\n`;
    block += `message="${message.replace(/"/g, '\\"')}"\n`;
    block += `[/ACTION]`;
    return block;
  };

  const handleInsert = () => {
    const block = generateActionBlock();
    if (block) {
      onInsert(block);
      onClose();
    } else {
      toast.error('Please select a contact with phone number and enter a message');
    }
  };

  const handleSubmit = async () => {
    if (!contact?.phone || !message.trim()) {
      toast.error('Please select a contact and enter a message');
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/prime/communications/send`, {
        channel: 'sms',
        recipient_name: contact.name,
        recipient_phone: contact.phone,
        message_body: message,
        related_job_id: job?.id,
        related_contact_id: contact.id,
        approved_by_user_id: 'super_admin',
        approved_via: 'QUICK_ACTION_SMS',
        override_quiet_hours: true
      });
      
      if (res.data.success) {
        toast.success('SMS sent successfully!');
        onSubmit?.(res.data);
        // Reset form
        setContact(null);
        setMessage('');
        setTemplate('');
        setJob(null);
        onClose();
      } else {
        toast.error(res.data.error || 'Failed to send SMS');
      }
    } catch (error) {
      toast.error('Failed to send SMS');
    }
    setSubmitting(false);
  };

  // SMS segment count (160 chars per segment)
  const segmentCount = Math.ceil(message.length / 160) || 1;
  const isMultiSegment = message.length > 160;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-green-500" />
            Send SMS
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Template Selection */}
          <div className="space-y-2">
            <Label>Template</Label>
            <Select value={template} onValueChange={handleTemplateChange}>
              <SelectTrigger data-testid="sms-template-select">
                <SelectValue placeholder="Choose a template (optional)" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(SMS_TEMPLATES).map(([key, tmpl]) => (
                  <SelectItem key={key || 'none'} value={key || 'none'}>
                    {tmpl.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <ContactPicker value={contact} onChange={setContact} label="Recipient *" />
          
          {contact && !contact.phone && (
            <p className="text-sm text-red-500">This contact has no phone number</p>
          )}
          
          <JobPicker value={job} onChange={setJob} />
          
          <div className="space-y-2">
            <Label>Message *</Label>
            <Textarea
              placeholder="Type your message..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              data-testid="sms-message"
            />
            <div className="flex justify-between text-xs">
              <span className={isMultiSegment ? 'text-orange-500' : 'text-muted-foreground'}>
                {isMultiSegment && `${segmentCount} segments • `}
                {message.length} characters
              </span>
              <span className="text-muted-foreground">
                {160 - (message.length % 160 || 160)} chars remaining in segment
              </span>
            </div>
          </div>
        </div>
        
        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={handleInsert}>
            Insert into Chat
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || !contact?.phone || !message.trim()}>
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
            Send Now
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Email Composer - Enhanced with CC, BCC, templates, and priority
const EmailComposer = ({ open, onClose, onInsert, onSubmit }) => {
  const [contact, setContact] = useState(null);
  const [ccContacts, setCcContacts] = useState([]);
  const [job, setJob] = useState(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [priority, setPriority] = useState('normal');
  const [template, setTemplate] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const EMAIL_TEMPLATES = {
    '': { name: 'No Template', subject: '', body: '' },
    'follow_up': { 
      name: 'Follow-up', 
      subject: 'Following up on our conversation',
      body: 'Hi [Name],\n\nI wanted to follow up on our recent conversation. Please let me know if you have any questions or if there\'s anything else I can help with.\n\nBest regards'
    },
    'appointment_confirm': { 
      name: 'Appointment Confirmation', 
      subject: 'Appointment Confirmation',
      body: 'Hi [Name],\n\nThis is to confirm your upcoming appointment:\n\nDate: [Date]\nTime: [Time]\nLocation: [Location]\n\nPlease let us know if you need to reschedule.\n\nThank you!'
    },
    'quote_ready': { 
      name: 'Quote Ready', 
      subject: 'Your Quote is Ready',
      body: 'Hi [Name],\n\nGreat news! Your quote is ready for review.\n\nPlease find the details attached. If you have any questions or would like to proceed, don\'t hesitate to reach out.\n\nBest regards'
    },
    'thank_you': { 
      name: 'Thank You', 
      subject: 'Thank You!',
      body: 'Hi [Name],\n\nThank you for choosing us! We truly appreciate your business.\n\nIf you need anything, we\'re just a call or email away.\n\nWarm regards'
    }
  };

  const handleTemplateChange = (templateKey) => {
    setTemplate(templateKey);
    if (EMAIL_TEMPLATES[templateKey]) {
      if (EMAIL_TEMPLATES[templateKey].subject) setSubject(EMAIL_TEMPLATES[templateKey].subject);
      if (EMAIL_TEMPLATES[templateKey].body) setBody(EMAIL_TEMPLATES[templateKey].body);
    }
  };

  const generateActionBlock = () => {
    if (!contact?.email || !subject.trim() || !body.trim()) return null;
    
    let block = `[ACTION:EMAIL]\n`;
    block += `to_contact_id=${contact.id}\n`;
    block += `to_email=${contact.email}\n`;
    block += `subject="${subject.replace(/"/g, '\\"')}"\n`;
    if (ccContacts.length > 0) {
      block += `cc_emails="${ccContacts.map(c => c.email).join(',')}"\n`;
    }
    block += `priority="${priority}"\n`;
    if (job) block += `job_id=${job.id}\n`;
    block += `message="${body.replace(/"/g, '\\"')}"\n`;
    block += `[/ACTION]`;
    return block;
  };

  const handleInsert = () => {
    const block = generateActionBlock();
    if (block) {
      onInsert(block);
      onClose();
    } else {
      toast.error('Please fill in all required fields');
    }
  };

  const handleSubmit = async () => {
    if (!contact?.email || !subject.trim() || !body.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/prime/communications/send`, {
        channel: 'email',
        recipient_name: contact.name,
        recipient_email: contact.email,
        cc_emails: ccContacts.map(c => c.email),
        subject: subject,
        message_body: body,
        priority: priority,
        related_job_id: job?.id,
        related_contact_id: contact.id,
        approved_by_user_id: 'super_admin',
        approved_via: 'QUICK_ACTION_EMAIL'
      });
      
      if (res.data.success) {
        toast.success('Email sent successfully!');
        onSubmit?.(res.data);
        // Reset form
        setContact(null);
        setCcContacts([]);
        setSubject('');
        setBody('');
        setPriority('normal');
        setTemplate('');
        setJob(null);
        onClose();
      } else {
        toast.error(res.data.error || 'Failed to send email');
      }
    } catch (error) {
      toast.error('Failed to send email');
    }
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-blue-500" />
            Send Email
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Template Selection */}
          <div className="space-y-2">
            <Label>Template</Label>
            <Select value={template} onValueChange={handleTemplateChange}>
              <SelectTrigger data-testid="email-template-select">
                <SelectValue placeholder="Choose a template (optional)" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(EMAIL_TEMPLATES).map(([key, tmpl]) => (
                  <SelectItem key={key || 'none'} value={key || 'none'}>
                    {tmpl.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <ContactPicker value={contact} onChange={setContact} label="To *" />
          
          {contact && !contact.email && (
            <p className="text-sm text-red-500">This contact has no email address</p>
          )}
          
          {/* CC - Multiple contacts */}
          <div className="space-y-2">
            <Label>CC (optional)</Label>
            <div className="text-xs text-muted-foreground mb-1">
              {ccContacts.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {ccContacts.map((cc, idx) => (
                    <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                      {cc.name || cc.email}
                      <button onClick={() => setCcContacts(ccContacts.filter((_, i) => i !== idx))} className="hover:text-red-500">×</button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <ContactPicker 
              value={null} 
              onChange={(c) => {
                if (c && c.email && !ccContacts.find(cc => cc.id === c.id)) {
                  setCcContacts([...ccContacts, c]);
                }
              }} 
              label="" 
            />
          </div>
          
          <div className="space-y-2">
            <Label>Subject *</Label>
            <Input
              placeholder="Email subject..."
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              data-testid="email-subject"
            />
          </div>

          {/* Priority */}
          <div className="space-y-2">
            <Label>Priority</Label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger data-testid="email-priority-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">🟢 Low</SelectItem>
                <SelectItem value="normal">🟡 Normal</SelectItem>
                <SelectItem value="high">🟠 High</SelectItem>
                <SelectItem value="urgent">🔴 Urgent</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <JobPicker value={job} onChange={setJob} />
          
          <div className="space-y-2">
            <Label>Body *</Label>
            <Textarea
              placeholder="Email body..."
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={6}
              data-testid="email-body"
            />
          </div>
        </div>
        
        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={handleInsert}>
            Insert into Chat
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || !contact?.email || !subject || !body}>
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
            Send Email
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Calendar Event Composer - Enhanced with location, description, attendees, reminders
const EventComposer = ({ open, onClose, onInsert, onSubmit }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('10:00');
  const [calendarTarget, setCalendarTarget] = useState('super_admin');
  const [eventType, setEventType] = useState('meeting');
  const [reminder, setReminder] = useState('30');
  const [attendees, setAttendees] = useState([]);
  const [contact, setContact] = useState(null);
  const [job, setJob] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const EVENT_TYPES = {
    meeting: { name: 'Meeting', icon: '👥' },
    call: { name: 'Phone Call', icon: '📞' },
    site_visit: { name: 'Site Visit', icon: '🏠' },
    follow_up: { name: 'Follow-up', icon: '📋' },
    deadline: { name: 'Deadline', icon: '⏰' },
    other: { name: 'Other', icon: '📌' }
  };

  const REMINDER_OPTIONS = [
    { value: '0', label: 'At time of event' },
    { value: '15', label: '15 minutes before' },
    { value: '30', label: '30 minutes before' },
    { value: '60', label: '1 hour before' },
    { value: '1440', label: '1 day before' },
  ];

  const generateActionBlock = () => {
    if (!title.trim() || !date) return null;
    
    const startDateTime = `${date}T${startTime}:00`;
    const endDateTime = `${date}T${endTime}:00`;
    
    let block = `[ACTION:EVENT]\n`;
    block += `title="${title.replace(/"/g, '\\"')}"\n`;
    block += `start="${startDateTime}"\n`;
    block += `end="${endDateTime}"\n`;
    block += `event_type="${eventType}"\n`;
    block += `calendar_target="${calendarTarget}"\n`;
    if (description) block += `description="${description.replace(/"/g, '\\"')}"\n`;
    if (location) block += `location="${location.replace(/"/g, '\\"')}"\n`;
    block += `reminder_minutes=${reminder}\n`;
    if (attendees.length > 0) {
      block += `attendees="${attendees.map(a => a.email || a.name).join(',')}"\n`;
    }
    if (job) block += `job_id=${job.id}\n`;
    if (contact) block += `contact_id=${contact.id}\n`;
    block += `[/ACTION]`;
    return block;
  };

  const handleInsert = () => {
    const block = generateActionBlock();
    if (block) {
      onInsert(block);
      onClose();
    } else {
      toast.error('Please fill in title and date');
    }
  };

  const handleSubmit = async () => {
    if (!title.trim() || !date) {
      toast.error('Please fill in title and date');
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/prime/calendar/create`, {
        title,
        description,
        location,
        start_datetime: `${date}T${startTime}:00`,
        end_datetime: `${date}T${endTime}:00`,
        timezone: 'America/New_York',
        calendar_target: calendarTarget,
        event_type: eventType,
        reminder_minutes: parseInt(reminder),
        attendees: attendees.map(a => ({ name: a.name, email: a.email })),
        related_job_id: job?.id,
        related_contact_id: contact?.id,
        created_by_user_id: 'super_admin',
        created_by_ai: 'prime'
      });
      
      if (res.data.success) {
        toast.success('Event created successfully!');
        onSubmit?.(res.data);
        // Reset form
        setTitle('');
        setDescription('');
        setLocation('');
        setDate('');
        setStartTime('09:00');
        setEndTime('10:00');
        setEventType('meeting');
        setReminder('30');
        setAttendees([]);
        setContact(null);
        setJob(null);
        onClose();
      } else {
        toast.error(res.data.error || 'Failed to create event');
      }
    } catch (error) {
      toast.error('Failed to create event');
    }
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-purple-500" />
            Create Calendar Event
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Title */}
          <div className="space-y-2">
            <Label>Title *</Label>
            <Input
              placeholder="Event title..."
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              data-testid="event-title"
            />
          </div>
          
          {/* Event Type & Calendar */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Event Type</Label>
              <Select value={eventType} onValueChange={setEventType}>
                <SelectTrigger data-testid="event-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(EVENT_TYPES).map(([key, type]) => (
                    <SelectItem key={key} value={key}>
                      {type.icon} {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Calendar</Label>
              <Select value={calendarTarget} onValueChange={setCalendarTarget}>
                <SelectTrigger data-testid="event-calendar-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="super_admin">My Calendar</SelectItem>
                  <SelectItem value="team">Team Calendar</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Date, Start & End Time */}
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label>Date *</Label>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                data-testid="event-date"
              />
            </div>
            <div className="space-y-2">
              <Label>Start</Label>
              <Input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                data-testid="event-start-time"
              />
            </div>
            <div className="space-y-2">
              <Label>End</Label>
              <Input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                data-testid="event-end-time"
              />
            </div>
          </div>

          {/* Location */}
          <div className="space-y-2">
            <Label>Location</Label>
            <Input
              placeholder="Add location or video call link..."
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              data-testid="event-location"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              placeholder="Add details, agenda, or notes..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              data-testid="event-description"
            />
          </div>

          {/* Attendees */}
          <div className="space-y-2">
            <Label>Attendees</Label>
            {attendees.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {attendees.map((att, idx) => (
                  <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs">
                    {att.name || att.email}
                    <button onClick={() => setAttendees(attendees.filter((_, i) => i !== idx))} className="hover:text-red-500">×</button>
                  </span>
                ))}
              </div>
            )}
            <UserPicker 
              value={null} 
              onChange={(u) => {
                if (u && !attendees.find(a => a.id === u.id)) {
                  setAttendees([...attendees, { id: u.id, name: u.name || `${u.first_name} ${u.last_name}`, email: u.email }]);
                }
              }} 
              label="" 
            />
          </div>

          {/* Reminder */}
          <div className="space-y-2">
            <Label>Reminder</Label>
            <Select value={reminder} onValueChange={setReminder}>
              <SelectTrigger data-testid="event-reminder-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {REMINDER_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <JobPicker value={job} onChange={setJob} />
        </div>
        
        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={handleInsert}>
            Insert into Chat
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || !title || !date}>
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
            Create Event
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Task Composer - Enhanced with full task capabilities
const TaskComposer = ({ open, onClose, onInsert, onSubmit }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [assignee, setAssignee] = useState(null);
  const [dueDate, setDueDate] = useState('');
  const [priority, setPriority] = useState('medium');
  const [category, setCategory] = useState('general');
  const [estimatedTime, setEstimatedTime] = useState('');
  const [project, setProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [contact, setContact] = useState(null);
  const [job, setJob] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);

  // Categories for task organization
  const TASK_CATEGORIES = {
    followups: { name: 'Follow-ups', color: 'bg-blue-500/10 text-blue-500' },
    approvals: { name: 'Approvals', color: 'bg-purple-500/10 text-purple-500' },
    scheduling: { name: 'Scheduling', color: 'bg-orange-500/10 text-orange-500' },
    communications: { name: 'Communications', color: 'bg-cyan-500/10 text-cyan-500' },
    general: { name: 'General', color: 'bg-gray-500/10 text-gray-500' }
  };

  // Fetch projects on open
  useEffect(() => {
    if (open) {
      const fetchProjects = async () => {
        setLoadingProjects(true);
        try {
          const res = await axios.get(`${API}/projects`);
          // Flatten projects with hierarchy
          const flattenProjects = (items, depth = 0) => {
            let flat = [];
            for (const item of items) {
              flat.push({ ...item, depth });
              if (item.children?.length > 0) {
                flat = flat.concat(flattenProjects(item.children, depth + 1));
              }
            }
            return flat;
          };
          setProjects(flattenProjects(res.data || []));
        } catch (error) {
          console.error('Failed to fetch projects:', error);
        }
        setLoadingProjects(false);
      };
      fetchProjects();
    }
  }, [open]);

  const generateActionBlock = () => {
    if (!title.trim()) return null;
    
    let block = `[ACTION:TASK]\n`;
    block += `title="${title.replace(/"/g, '\\"')}"\n`;
    if (description) block += `description="${description.replace(/"/g, '\\"')}"\n`;
    if (assignee) block += `assignee_user_id=${assignee.id}\n`;
    if (dueDate) block += `due="${dueDate}"\n`;
    block += `priority="${priority}"\n`;
    block += `category="${category}"\n`;
    if (estimatedTime) block += `estimated_time="${estimatedTime}"\n`;
    if (project) block += `project_id=${project.id}\n`;
    if (job) block += `job_id=${job.id}\n`;
    if (contact) block += `contact_id=${contact.id}\n`;
    block += `[/ACTION]`;
    return block;
  };

  const handleInsert = () => {
    const block = generateActionBlock();
    if (block) {
      onInsert(block);
      onClose();
    } else {
      toast.error('Please enter a task title');
    }
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast.error('Please enter a task title');
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/tasks`, {
        title,
        description,
        assigned_to: assignee?.id,
        assignees: assignee ? [{ id: assignee.id, name: assignee.name || assignee.first_name + ' ' + assignee.last_name }] : [],
        due_date: dueDate || null,
        priority,
        category,
        estimated_time: estimatedTime || null,
        project_id: project?.id || null,
        job_id: job?.id,
        contact_id: contact?.id,
        created_by: 'prime',
        tags: ['prime-created'],
        status: 'pending'
      });
      
      toast.success('Task created successfully!');
      onSubmit?.(res.data);
      // Reset form
      setTitle('');
      setDescription('');
      setAssignee(null);
      setDueDate('');
      setPriority('medium');
      setCategory('general');
      setEstimatedTime('');
      setProject(null);
      setContact(null);
      setJob(null);
      onClose();
    } catch (error) {
      toast.error('Failed to create task');
    }
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ListTodo className="h-5 w-5 text-orange-500" />
            Create Task
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Task Title */}
          <div className="space-y-2">
            <Label>Task Title *</Label>
            <Input
              placeholder="What needs to be done?"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              data-testid="task-title-input"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              placeholder="Add details, instructions, or context..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              data-testid="task-description-input"
            />
          </div>
          
          {/* Assignee */}
          <UserPicker value={assignee} onChange={setAssignee} label="Assignee" />
          
          {/* Project Selection */}
          <div className="space-y-2">
            <Label>Project</Label>
            <Select value={project?.id || 'none'} onValueChange={(val) => {
              if (val === 'none') {
                setProject(null);
              } else {
                const found = projects.find(p => p.id === val);
                setProject(found || null);
              }
            }}>
              <SelectTrigger data-testid="task-project-select">
                <SelectValue placeholder={loadingProjects ? "Loading..." : "Select project (optional)"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">
                  <span className="text-muted-foreground">No Project (Inbox)</span>
                </SelectItem>
                {projects.map(p => (
                  <SelectItem key={p.id} value={p.id}>
                    <span style={{ paddingLeft: `${p.depth * 12}px` }} className="flex items-center gap-2">
                      <span 
                        className="w-2 h-2 rounded-sm flex-shrink-0" 
                        style={{ backgroundColor: p.color || '#3b82f6' }}
                      />
                      {p.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Due Date & Priority */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Due Date</Label>
              <Input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                data-testid="task-due-date"
              />
            </div>
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger data-testid="task-priority-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">🟢 Low</SelectItem>
                  <SelectItem value="medium">🟡 Medium</SelectItem>
                  <SelectItem value="high">🟠 High</SelectItem>
                  <SelectItem value="urgent">🔴 Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Category & Estimated Time */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="task-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(TASK_CATEGORIES).map(([key, cat]) => (
                    <SelectItem key={key} value={key}>
                      <span className={`px-2 py-0.5 rounded text-xs ${cat.color}`}>
                        {cat.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Est. Time</Label>
              <Input
                placeholder="e.g., 2 hours"
                value={estimatedTime}
                onChange={(e) => setEstimatedTime(e.target.value)}
                data-testid="task-estimated-time"
              />
            </div>
          </div>
          
          {/* Link to Job */}
          <JobPicker value={job} onChange={setJob} />
        </div>
        
        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={handleInsert}>
            Insert into Chat
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || !title}>
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
            Create Task
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Note Composer - Enhanced with categories, tags, and project linking
const NoteComposer = ({ open, onClose, onInsert, onSubmit }) => {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [category, setCategory] = useState('general');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [project, setProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [contact, setContact] = useState(null);
  const [job, setJob] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);

  const NOTE_CATEGORIES = {
    general: { name: 'General', color: 'bg-gray-500/10 text-gray-500' },
    meeting: { name: 'Meeting Notes', color: 'bg-blue-500/10 text-blue-500' },
    call: { name: 'Call Notes', color: 'bg-green-500/10 text-green-500' },
    idea: { name: 'Ideas', color: 'bg-yellow-500/10 text-yellow-500' },
    research: { name: 'Research', color: 'bg-purple-500/10 text-purple-500' },
    reminder: { name: 'Reminder', color: 'bg-orange-500/10 text-orange-500' }
  };

  // Fetch projects on open
  useEffect(() => {
    if (open) {
      const fetchProjects = async () => {
        setLoadingProjects(true);
        try {
          const res = await axios.get(`${API}/projects`);
          const flattenProjects = (items, depth = 0) => {
            let flat = [];
            for (const item of items) {
              flat.push({ ...item, depth });
              if (item.children?.length > 0) {
                flat = flat.concat(flattenProjects(item.children, depth + 1));
              }
            }
            return flat;
          };
          setProjects(flattenProjects(res.data || []));
        } catch (error) {
          console.error('Failed to fetch projects:', error);
        }
        setLoadingProjects(false);
      };
      fetchProjects();
    }
  }, [open]);

  const addTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    setTags(tags.filter(t => t !== tagToRemove));
  };

  const generateActionBlock = () => {
    if (!title.trim() || !body.trim()) return null;
    
    let block = `[ACTION:NOTE]\n`;
    block += `title="${title.replace(/"/g, '\\"')}"\n`;
    block += `body="${body.replace(/"/g, '\\"')}"\n`;
    block += `category="${category}"\n`;
    if (tags.length > 0) block += `tags="${tags.join(',')}"\n`;
    if (project) block += `project_id=${project.id}\n`;
    if (job) block += `job_id=${job.id}\n`;
    if (contact) block += `contact_id=${contact.id}\n`;
    block += `[/ACTION]`;
    return block;
  };

  const handleInsert = () => {
    const block = generateActionBlock();
    if (block) {
      onInsert(block);
      onClose();
    } else {
      toast.error('Please fill in title and body');
    }
  };

  const handleSubmit = async () => {
    if (!title.trim() || !body.trim()) {
      toast.error('Please fill in title and body');
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/notes`, {
        title,
        content: body,
        category,
        tags,
        project_id: project?.id,
        job_id: job?.id,
        contact_id: contact?.id,
        created_by: 'prime'
      });
      
      toast.success('Note created successfully!');
      onSubmit?.(res.data);
      // Reset form
      setTitle('');
      setBody('');
      setCategory('general');
      setTags([]);
      setProject(null);
      setContact(null);
      setJob(null);
      onClose();
    } catch (error) {
      toast.error('Failed to create note');
    }
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-cyan-500" />
            Create Note
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Title *</Label>
            <Input
              placeholder="Note title..."
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              data-testid="note-title"
            />
          </div>

          {/* Category */}
          <div className="space-y-2">
            <Label>Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="note-category-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(NOTE_CATEGORIES).map(([key, cat]) => (
                  <SelectItem key={key} value={key}>
                    <span className={`px-2 py-0.5 rounded text-xs ${cat.color}`}>
                      {cat.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label>Content *</Label>
            <Textarea
              placeholder="Note content..."
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={5}
              data-testid="note-body"
            />
          </div>

          {/* Tags */}
          <div className="space-y-2">
            <Label>Tags</Label>
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {tags.map((tag, idx) => (
                  <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 bg-cyan-100 text-cyan-700 rounded text-xs">
                    #{tag}
                    <button onClick={() => removeTag(tag)} className="hover:text-red-500">×</button>
                  </span>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <Input
                placeholder="Add tag..."
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                className="flex-1"
              />
              <Button type="button" variant="outline" size="sm" onClick={addTag}>Add</Button>
            </div>
          </div>

          {/* Project Selection */}
          <div className="space-y-2">
            <Label>Link to Project</Label>
            <Select value={project?.id || 'none'} onValueChange={(val) => {
              if (val === 'none') {
                setProject(null);
              } else {
                const found = projects.find(p => p.id === val);
                setProject(found || null);
              }
            }}>
              <SelectTrigger data-testid="note-project-select">
                <SelectValue placeholder={loadingProjects ? "Loading..." : "Select project (optional)"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">
                  <span className="text-muted-foreground">No Project</span>
                </SelectItem>
                {projects.map(p => (
                  <SelectItem key={p.id} value={p.id}>
                    <span style={{ paddingLeft: `${p.depth * 12}px` }} className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-sm" style={{ backgroundColor: p.color || '#3b82f6' }} />
                      {p.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <JobPicker value={job} onChange={setJob} />
        </div>
        
        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={handleInsert}>
            Insert into Chat
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || !title || !body}>
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
            Create Note
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Channel Message Composer - Enhanced with more channels, mentions, and formatting
const ChannelComposer = ({ open, onClose, onInsert }) => {
  const [channelId, setChannelId] = useState('');
  const [message, setMessage] = useState('');
  const [priority, setPriority] = useState('normal');
  const [mentions, setMentions] = useState([]);
  const [job, setJob] = useState(null);

  const CHANNELS = [
    { id: 'general', name: '#general', description: 'Company-wide announcements' },
    { id: 'sales', name: '#sales', description: 'Sales team updates' },
    { id: 'operations', name: '#operations', description: 'Operations coordination' },
    { id: 'marketing', name: '#marketing', description: 'Marketing campaigns' },
    { id: 'support', name: '#support', description: 'Customer support' },
    { id: 'random', name: '#random', description: 'Water cooler chat' }
  ];

  const generateActionBlock = () => {
    if (!channelId || !message.trim()) return null;
    
    let block = `[ACTION:CHANNEL_MESSAGE]\n`;
    block += `channel_id=${channelId}\n`;
    block += `message="${message.replace(/"/g, '\\"')}"\n`;
    block += `priority="${priority}"\n`;
    if (mentions.length > 0) {
      block += `mentions="${mentions.map(m => m.id).join(',')}"\n`;
    }
    if (job) block += `job_id=${job.id}\n`;
    block += `[/ACTION]`;
    return block;
  };

  const handleInsert = () => {
    const block = generateActionBlock();
    if (block) {
      onInsert(block);
      onClose();
    } else {
      toast.error('Please select a channel and enter a message');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Hash className="h-5 w-5 text-indigo-500" />
            Post to Channel
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Channel *</Label>
            <Select value={channelId} onValueChange={setChannelId}>
              <SelectTrigger data-testid="channel-select">
                <SelectValue placeholder="Select channel..." />
              </SelectTrigger>
              <SelectContent>
                {CHANNELS.map(ch => (
                  <SelectItem key={ch.id} value={ch.id}>
                    <div className="flex flex-col">
                      <span className="font-medium">{ch.name}</span>
                      <span className="text-xs text-muted-foreground">{ch.description}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Priority */}
          <div className="space-y-2">
            <Label>Priority</Label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger data-testid="channel-priority-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">🟢 Low</SelectItem>
                <SelectItem value="normal">🟡 Normal</SelectItem>
                <SelectItem value="high">🟠 High - Notify all</SelectItem>
                <SelectItem value="urgent">🔴 Urgent - @everyone</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Mentions */}
          <div className="space-y-2">
            <Label>Mention Users</Label>
            {mentions.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {mentions.map((m, idx) => (
                  <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-100 text-indigo-700 rounded text-xs">
                    @{m.name}
                    <button onClick={() => setMentions(mentions.filter((_, i) => i !== idx))} className="hover:text-red-500">×</button>
                  </span>
                ))}
              </div>
            )}
            <UserPicker 
              value={null} 
              onChange={(u) => {
                if (u && !mentions.find(m => m.id === u.id)) {
                  setMentions([...mentions, { id: u.id, name: u.name || `${u.first_name} ${u.last_name}` }]);
                }
              }} 
              label="" 
            />
          </div>
          
          <div className="space-y-2">
            <Label>Message *</Label>
            <Textarea
              placeholder="Type your message..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              data-testid="channel-message"
            />
          </div>
          
          <JobPicker value={job} onChange={setJob} />
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={handleInsert}>
            Insert into Chat
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// DM Composer - Enhanced with read receipts and priority
const DMComposer = ({ open, onClose, onInsert }) => {
  const [user, setUser] = useState(null);
  const [message, setMessage] = useState('');
  const [priority, setPriority] = useState('normal');
  const [requestReadReceipt, setRequestReadReceipt] = useState(false);
  const [job, setJob] = useState(null);

  const generateActionBlock = () => {
    if (!user || !message.trim()) return null;
    
    let block = `[ACTION:DM]\n`;
    block += `user_id=${user.id}\n`;
    block += `message="${message.replace(/"/g, '\\"')}"\n`;
    block += `priority="${priority}"\n`;
    if (requestReadReceipt) block += `request_read_receipt=true\n`;
    if (job) block += `job_id=${job.id}\n`;
    block += `[/ACTION]`;
    return block;
  };

  const handleInsert = () => {
    const block = generateActionBlock();
    if (block) {
      onInsert(block);
      onClose();
    } else {
      toast.error('Please select a user and enter a message');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5 text-pink-500" />
            Direct Message
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <UserPicker value={user} onChange={setUser} label="Send to *" />

          {/* Priority */}
          <div className="space-y-2">
            <Label>Priority</Label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger data-testid="dm-priority-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">🟢 Low</SelectItem>
                <SelectItem value="normal">🟡 Normal</SelectItem>
                <SelectItem value="high">🟠 High</SelectItem>
                <SelectItem value="urgent">🔴 Urgent</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label>Message *</Label>
            <Textarea
              placeholder="Type your message..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              data-testid="dm-message"
            />
          </div>

          {/* Read Receipt */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="read-receipt"
              checked={requestReadReceipt}
              onChange={(e) => setRequestReadReceipt(e.target.checked)}
              className="rounded border-gray-300"
            />
            <Label htmlFor="read-receipt" className="text-sm font-normal cursor-pointer">
              Request read receipt
            </Label>
          </div>
          
          <JobPicker value={job} onChange={setJob} />
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={handleInsert}>
            Insert into Chat
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============== MAIN COMPONENT ==============

const PrimeQuickActions = ({ onInsertAction, onActionComplete }) => {
  const [activeComposer, setActiveComposer] = useState(null);

  const actions = [
    { id: 'sms', icon: MessageSquare, label: 'SMS', color: 'text-green-500' },
    { id: 'email', icon: Mail, label: 'Email', color: 'text-blue-500' },
    { id: 'event', icon: Calendar, label: 'Event', color: 'text-purple-500' },
    { id: 'task', icon: ListTodo, label: 'Task', color: 'text-orange-500' },
    { id: 'note', icon: FileText, label: 'Note', color: 'text-cyan-500' },
    { id: 'channel', icon: Hash, label: 'Channel', color: 'text-indigo-500' },
    { id: 'dm', icon: User, label: 'DM', color: 'text-pink-500' },
  ];

  const handleInsert = (actionBlock) => {
    onInsertAction?.(actionBlock);
  };

  const handleSubmit = (result) => {
    onActionComplete?.(result);
  };

  return (
    <>
      {/* Quick Actions Bar */}
      <div className="flex items-center gap-1 px-4 py-2 bg-slate-100/50 dark:bg-slate-800/50 border-b overflow-x-auto">
        <span className="text-xs text-muted-foreground font-medium mr-2 whitespace-nowrap">Quick Actions:</span>
        {actions.map((action) => (
          <QuickActionButton
            key={action.id}
            icon={action.icon}
            label={action.label}
            color={action.color}
            onClick={() => setActiveComposer(action.id)}
          />
        ))}
      </div>

      {/* Composers */}
      <SMSComposer
        open={activeComposer === 'sms'}
        onClose={() => setActiveComposer(null)}
        onInsert={handleInsert}
        onSubmit={handleSubmit}
      />
      
      <EmailComposer
        open={activeComposer === 'email'}
        onClose={() => setActiveComposer(null)}
        onInsert={handleInsert}
        onSubmit={handleSubmit}
      />
      
      <EventComposer
        open={activeComposer === 'event'}
        onClose={() => setActiveComposer(null)}
        onInsert={handleInsert}
        onSubmit={handleSubmit}
      />
      
      <TaskComposer
        open={activeComposer === 'task'}
        onClose={() => setActiveComposer(null)}
        onInsert={handleInsert}
        onSubmit={handleSubmit}
      />
      
      <NoteComposer
        open={activeComposer === 'note'}
        onClose={() => setActiveComposer(null)}
        onInsert={handleInsert}
        onSubmit={handleSubmit}
      />
      
      <ChannelComposer
        open={activeComposer === 'channel'}
        onClose={() => setActiveComposer(null)}
        onInsert={handleInsert}
      />
      
      <DMComposer
        open={activeComposer === 'dm'}
        onClose={() => setActiveComposer(null)}
        onInsert={handleInsert}
      />
    </>
  );
};

export default PrimeQuickActions;
